import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelToDisplay } from '@iotv/core-services';
import { AppValueTypes, getObjectHash, isNonNaNNumber, Milliseconds } from '@iotv/iotv-v3-types';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Button, Stack, Tooltip } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useContext, useEffect } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { ComponentMode } from '../../../types/AppTypes';
import { EditFieldArbitrator } from '../../factories/inputFields/InputFieldArbitrator';
import { ObservationsContext } from '../Contexts/OvesrvationsContextType_V2';
import { UseObservationsContext } from '../Contexts/UseObservationsContext';
import { PeriodBasisInput } from '../gridColFns';
import { ducktype } from '../helperFns';
import { getNextObservationInput, getNullMapFromKeys, ObservationInputContext, } from './AddObservationContext';
import { getObservationPeriod, ObservationInputMetaKeys } from '../fns';
import { HistoryStoreContext } from '../../../ContextProviders/HistoryStoreContext';
import { ObservationDataGrid } from '../ObservationDataGrid';
import { AppLocalStorage } from '../../../data/LocalStorage/AppLocalStorage';
import { NavSections } from '../../../components/navigation/NavSections';
const debug = process.env.REACT_APP_DEBUG && false;
export const AddObservations = (props) => {
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { state: ObservationsContextState, dispatch } = useContext(ObservationsContext);
    const { persistedObservationHistory, save, working, querying, errMessage } = useContext(UseObservationsContext);
    const { historyStoreFns: { setTimePeriod }, } = useContext(HistoryStoreContext);
    const { observationHistory, newObservation } = ObservationsContextState;
    const { state: observationInputState, dispatch: observationInputDispatch } = useContext(ObservationInputContext);
    const { currentObservationInput, currentInputIsValid, periodBasisMs, lastObservationInput } = observationInputState;
    const observationHistoryStore = observationHistory?.observedHistory ?? null;
    debug && console.log('historyStore', observationHistoryStore);
    const currentObservationPeriod = observationHistory && observationHistoryStore ? getObservationPeriod(observationHistory) : null;
    const currentObservationPeriodHash = getObjectHash(currentObservationPeriod);
    useEffect(() => {
        if (currentObservationPeriod) {
            console.log('TEMP Set timePeriod from observationHistory on load or when observation period changes', { currentObservationPeriod });
            setTimePeriod({ fromDateValue: dayjs(currentObservationPeriod.fromTimestampMs), toDateValue: dayjs(currentObservationPeriod.toTimestampMs) });
        }
        else {
            console.log('TEMP No observation period found in history, not setting timePeriod');
        }
    }, [currentObservationPeriodHash]);
    const observationKeys = (observationHistoryStore?.keys() ?? []).filter((k) => !ObservationInputMetaKeys.includes(k));
    const observationKeysHash = getObjectHash(observationKeys);
    useEffect(() => {
        const action = { type: 'changeObservationInputKeys', value: { observationKeys } };
        observationInputDispatch(action);
    }, [observationKeysHash]);
    useEffect(() => {
        if (persistedObservationHistory && !currentObservationInput) {
            console.log(`TEMP, iinitializing observation input state based on persisted observation history ${persistedObservationHistory.sk}`);
            const lastPersistedSnapshot = persistedObservationHistory.observedHistory.getStateSnapshotAt(-1);
            if (lastPersistedSnapshot) {
                const { timestamp, state } = lastPersistedSnapshot;
                const { observationType = 'POINT_IN_TIME', fromTimestampMs, toTimestampMs, description, periodBasisMs = null, ...stateOthers } = state;
                const lastObservationInput = {
                    timestamp,
                    fromTimestampMs,
                    toTimestampMs,
                    state: { ...stateOthers },
                    observationType: observationType,
                    description,
                    periodBasisMs,
                };
                if (lastObservationInput.fromTimestampMs === null) {
                    lastObservationInput.fromTimestampMs = timestamp + Milliseconds.MINUTE * 5;
                }
                const currentObservationInput = getNextObservationInput(lastObservationInput);
                const action = {
                    type: 'reInitialize',
                    value: { periodBasisMs, currentInputIsValid: false, observationKeys, lastObservationInput, currentObservationInput },
                };
                console.log(`TEMP, dispatching action to initialize observation input state based on persisted observation history`, { action });
                observationInputDispatch(action);
            }
            else {
                console.log(`TEMP, not initializing observation input state based on persisted observation history, because there is no persisted observation snapshot to use as a basis for initialization.`, persistedObservationHistory.observedHistory);
                const currentObservationInput = {
                    timestamp: Date.now(),
                    fromTimestampMs: Date.now(),
                    toTimestampMs: Date.now() + (periodBasisMs ?? Milliseconds.MINUTE * 5),
                    state: getNullMapFromKeys(observationKeys),
                    observationType: 'DEFINED_PERIOD',
                    description: '',
                    periodBasisMs,
                };
                const action = {
                    type: 'reInitialize',
                    value: { periodBasisMs, currentInputIsValid: false, observationKeys, lastObservationInput: null, currentObservationInput },
                };
                console.log(`TEMP, dispatching action to initialize observation input state based on persisted observation history`, { action });
                observationInputDispatch(action);
            }
        }
        else {
            console.log(`TEMP, not initializing observation input state based on persisted observation history, either because there is no persisted history, or because there is already a currentObservationInput in state.`, { persistedObservationHistory, currentObservationInput });
        }
    }, [persistedObservationHistory]);
    const handleAddObservation = () => {
        if (currentObservationInput) {
            const action = { type: 'addObservation', value: currentObservationInput };
            dispatch(action);
            const observationInputAction = { type: 'addObservationInput', value: { observationInput: currentObservationInput } };
            observationInputDispatch(observationInputAction);
        }
    };
    const handleChangeNewObservationStateAttribute = (event, k) => {
        if (currentObservationInput) {
            const target = event.currentTarget;
            const { type, checked, value } = target;
            const parsedValue = getParsedValue({ type, value, checked });
            const newObservationClone = {
                ...currentObservationInput,
                state: { ...currentObservationInput.state, [k]: parsedValue },
            };
            const action = { type: 'changeCurrentObservationInput', value: { observationInput: newObservationClone } };
            observationInputDispatch(action);
        }
    };
    const handleChangeFromTimestampMs = (event) => {
        if (currentObservationInput) {
            const target = event.currentTarget;
            const { type, checked, value } = target;
            const parsedValue = getParsedValue({ type, value, checked });
            if (isNonNaNNumber(parsedValue) && isNonNaNNumber(periodBasisMs)) {
                const newObservationClone = {
                    ...currentObservationInput,
                    fromTimestampMs: parsedValue,
                    toTimestampMs: parsedValue && periodBasisMs ? parsedValue + periodBasisMs : currentObservationInput.toTimestampMs,
                    state: {
                        ...currentObservationInput.state,
                    },
                };
                // console.log('TEMP handleChangeFromTimestampMs got event', { event, parsedValue, newObservationClone })
                console.log('TEMP handleChangeFromTimestampMs!', {
                    newFromDate: isNonNaNNumber(newObservationClone.state.fromTimestampMs)
                        ? new Date(newObservationClone.state.fromTimestampMs).toISOString()
                        : newObservationClone.state.fromTimestampMs,
                    newToDate: isNonNaNNumber(newObservationClone.state.toTimestampMs)
                        ? new Date(newObservationClone.state.toTimestampMs).toISOString()
                        : newObservationClone.state.toTimestampMs,
                });
                const action = { type: 'changeCurrentObservationInput', value: { observationInput: newObservationClone } };
                observationInputDispatch(action);
            }
        }
    };
    const handleChangeNewObservationTopLevlAttribute = (event, k) => {
        const target = event.currentTarget;
        const { type, checked, value } = target;
        const parsedValue = getParsedValue({ type, value, checked });
        const newObservationClone = {
            ...currentObservationInput,
            [k]: parsedValue,
        };
        const action = { type: 'changeCurrentObservationInput', value: { observationInput: newObservationClone } };
        observationInputDispatch(action);
    };
    const changePeriodBasis = ({ observationType, periodBasisMs: periodBasisMsIn }) => {
        let periodBasisMs = periodBasisMsIn;
        if (observationType === 'DEFINED_PERIOD' && periodBasisMs === null) {
            periodBasisMs = Milliseconds.MINUTE * 5;
        }
        else {
            // ??
        }
        const action = { type: 'setIsBasis', value: { observationType, periodBasisMs, lastObservationInput } };
        observationInputDispatch(action);
        if (isNonNaNNumber(periodBasisMs)) {
            AppLocalStorage.set(NavSections.THINGVIEW, 'observationsPeriodBasisMs', periodBasisMs);
        }
    };
    const getParsedValue = ({ type, value, checked }) => {
        debug && console.log('in updateKV after decomp', { type, checked, value });
        let parsedValue = value;
        switch (type) {
            case 'number': {
                const intermediateParsedValue = parseFloat(value) || parseInt(value);
                parsedValue = isNaN(intermediateParsedValue) ? undefined : intermediateParsedValue;
                break;
            }
            case 'checkbox':
                parsedValue = checked === true;
                break;
            default:
                break;
        }
        return parsedValue;
    };
    const getStaticFields = (currentObservationInput) => {
        const { observationType, description, fromTimestampMs, toTimestampMs, timestamp } = currentObservationInput;
        return [
            _jsx(EditFieldArbitrator, { tableOb: currentObservationInput,
                viewKeyDefinition: {
                    editable: true,
                    key: 'observationType',
                    type: AppValueTypes.ENUM,
                    label: 'Basis',
                    enumKV: {
                        Time: 'POINT_IN_TIME',
                        Period: 'DEFINED_PERIOD',
                    },
                },
                updateKV: (event, k) => {
                    const parsedValue = getParsedValue(event.currentTarget);
                    changePeriodBasis({ observationType: parsedValue, periodBasisMs });
                },
                mode: ComponentMode.EDIT,
                textFieldProps: {
                    variant: 'outlined',
                    size: 'small',
                    InputLabelProps: {
                        shrink: true,
                    },
                } }, 'observationType'),
            _jsx(DateTimePicker, { maxDate: observationType === 'POINT_IN_TIME' && toTimestampMs ? dayjs(toTimestampMs) : undefined,
                size: 'small',
                value: dayjs(fromTimestampMs).isValid() ? dayjs(fromTimestampMs) : null,
                onChange: (date) => {
                    const event = { currentTarget: { type: 'datetime-local', value: date?.valueOf() } };
                    const value = event.currentTarget.value;
                    if (isNonNaNNumber(value)) {
                        if (toTimestampMs && value > toTimestampMs) {
                            alert('From time cannot be after To time');
                        }
                        else {
                            handleChangeFromTimestampMs(event);
                        }
                    }
                },
                label: 'From',
                slotProps: {
                    textField: {
                        variant: 'outlined',
                        size: 'small',
                    },
                } }, 'fromTimestampMs'),
            observationType === 'DEFINED_PERIOD' ? (_jsx(PeriodBasisInput, { value: periodBasisMs, setValue: (ms) => changePeriodBasis({ observationType, periodBasisMs: ms }) })) : (_jsx(DateTimePicker, { minDate: fromTimestampMs ? dayjs(fromTimestampMs) : undefined,
                size: 'small',
                value: dayjs(toTimestampMs).isValid() ? dayjs(toTimestampMs) : null,
                onChange: (date) => {
                    const event = { currentTarget: { type: 'datetime-local', value: date?.valueOf() } };
                    if (isNonNaNNumber(event.currentTarget.value)) {
                        handleChangeNewObservationTopLevlAttribute(event, 'toTimestampMs');
                    }
                },
                label: 'To',
                slotProps: {
                    textField: {
                        variant: 'outlined',
                        size: 'small',
                    },
                } }, 'toTimestampMs')),
        ];
    };
    const enableAdd = currentObservationInput &&
        currentInputIsValid &&
        !working &&
        !!(currentObservationInput?.description || Object.values(currentObservationInput.state).some((v) => v !== null && v !== undefined));
    const stateObjectTable = (_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsxs(Stack, { direction: 'column', alignItems: 'center', spacing: 2, padding: 2, children: [currentObservationInput && (_jsxs(Stack, { width: '100%', spacing: 2, direction: 'column', children: [observationKeys.length === 0 && _jsx(Alert, { severity: 'warning', children: 'Use the Manage Keys tab to add measurement keys' }), _jsx(Stack, { direction: 'row', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', children: [
                                ...getStaticFields(currentObservationInput),
                                ...Object.keys(currentObservationInput.state)
                                    .filter((key) => !ObservationInputMetaKeys.includes(key))
                                    .map((key) => {
                                    const viewKeyDefinition = {
                                        editable: true,
                                        key,
                                        type: ducktype(key, newObservation?.[key]),
                                        label: camelToDisplay(key.replaceAll('_', ' ')),
                                    };
                                    return (_jsx(Stack, { children: _jsx(EditFieldArbitrator, { keyToUse: key,
                                            tableOb: currentObservationInput.state,
                                            mode: ComponentMode.EDIT,
                                            viewKeyDefinition,
                                            updateKV: handleChangeNewObservationStateAttribute,
                                            textFieldProps: {
                                                margin: 'none',
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            } }, key) }, key));
                                }),
                                _jsx(EditFieldArbitrator, { tableOb: currentObservationInput,
                                    viewKeyDefinition: {
                                        editable: true,
                                        key: 'description',
                                        type: AppValueTypes.STRING,
                                        label: 'Description',
                                    },
                                    updateKV: (event, k) => handleChangeNewObservationTopLevlAttribute(event, k),
                                    mode: ComponentMode.EDIT,
                                    textFieldProps: {
                                        margin: 'none',
                                        variant: 'outlined',
                                        InputLabelProps: {
                                            shrink: true,
                                        },
                                    } }, 'description'),
                            ] }, `ObsTableStack_1`), _jsx(Tooltip, { title: observationKeys.length === 0 ? 'Add one or more columns, then use this control to add values ' : 'Add an observation with these values', arrow: true, children: _jsx(Stack, { children: _jsx(Stack, { direction: 'row', justifyContent: 'left', alignItems: 'center', children: _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), "aria-label": "addObservation", disabled: !enableAdd, onClick: handleAddObservation, children: "Add" }) }) }) })] }, `ObsTableStack_0`)), _jsx(ObservationDataGrid, {})] }, "ObsTable") }));
    return stateObjectTable;
};
